import { API_BASE_URL } from "@/api/helpers";
import { fetchWithAuth } from "@/utils/fetchWithAuth";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

interface UpdateBookingAmenitiesParams {
  bookingId: string;
  amenityIds: string[];
}

export function useUpdateBookingAmenitiesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ bookingId, amenityIds }: UpdateBookingAmenitiesParams) => {
      const response = await fetchWithAuth(`${API_BASE_URL}/api/booking/${bookingId}/amenities`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ amenityIds }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.details || error.error || "Failed to update amenities");
      }

      return response.json();
    },
    onMutate: async (variables) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey: ["booking", variables.bookingId] });

      // Snapshot the previous value
      const previousBooking = queryClient.getQueryData(["booking", variables.bookingId]);

      // Return context with the snapshotted value
      return { previousBooking };
    },
    onSuccess: (data, variables) => {
      // Invalidate booking query to refetch updated data from server
      queryClient.invalidateQueries({ queryKey: ["booking", variables.bookingId] });
      // R27 — the series default shows on every non-customised date of the per-date list.
      queryClient.invalidateQueries({ queryKey: ["occurrence-amenities", variables.bookingId] });

      // Show success toast
      toast.success(data.message || "Amenities updated successfully");
    },
    onError: (error: Error, variables, context) => {
      // Rollback to the previous value on error
      if (context?.previousBooking) {
        queryClient.setQueryData(["booking", variables.bookingId], context.previousBooking);
      }

      // Show error toast
      toast.error(error.message || "Failed to update amenities");
    },
  });
}
